import { Coordinate } from "@nova/protobuf"

export type XY = { x: number; y: number }
type Segment = { a: XY; b: XY }
export type BBox = { minX: number; minY: number; maxX: number; maxY: number }

export class BVHNode {
    bbox: BBox
    center?: Coordinate
    left: BVHNode | null = null
    right: BVHNode | null = null
    segments: Segment[] | null = null

    constructor(bbox: BBox) {
        this.bbox = bbox
    }
}

function merge(a: BBox, b: BBox): BBox {
    return {
        minX: Math.min(a.minX, b.minX),
        minY: Math.min(a.minY, b.minY),
        maxX: Math.max(a.maxX, b.maxX),
        maxY: Math.max(a.maxY, b.maxY),
    }
}

function segmentBBox(s: Segment): BBox {
    return {
        minX: Math.min(s.a.x, s.b.x),
        minY: Math.min(s.a.y, s.b.y),
        maxX: Math.max(s.a.x, s.b.x),
        maxY: Math.max(s.a.y, s.b.y),
    }
}

export function buildBVHFromSegments(segments: Segment[], maxLeaf = 8): BVHNode {
    let rootBBox = segmentBBox(segments[0])
    let minCx = (segments[0].a.x + segments[0].b.x) / 2
    let maxCx = minCx
    let minCy = (segments[0].a.y + segments[0].b.y) / 2
    let maxCy = minCy

    for (let i = 1; i < segments.length; i++) {
        const s = segments[i]
        rootBBox = merge(rootBBox, segmentBBox(s))
        const cx = (s.a.x + s.b.x) / 2
        const cy = (s.a.y + s.b.y) / 2
        if (cx < minCx) minCx = cx
        if (cx > maxCx) maxCx = cx
        if (cy < minCy) minCy = cy
        if (cy > maxCy) maxCy = cy
    }

    const node = new BVHNode(rootBBox)

    if (segments.length <= maxLeaf) {
        node.segments = segments
        return node
    }

    const axis: "x" | "y" = maxCx - minCx > maxCy - minCy ? "x" : "y"
    segments.sort((s1, s2) => {
        const c1 = (s1.a[axis] + s1.b[axis]) / 2
        const c2 = (s2.a[axis] + s2.b[axis]) / 2
        return c1 - c2
    })

    const mid = segments.length >>> 1
    node.left = buildBVHFromSegments(segments.slice(0, mid), maxLeaf)
    node.right = buildBVHFromSegments(segments.slice(mid), maxLeaf)
    return node
}

export function pointAABBDist2(p: XY, box: BBox): number {
    let dx = 0,
        dy = 0
    if (p.x < box.minX) dx = box.minX - p.x
    else if (p.x > box.maxX) dx = p.x - box.maxX
    if (p.y < box.minY) dy = box.minY - p.y
    else if (p.y > box.maxY) dy = p.y - box.maxY
    return Math.pow(dx, 2) + Math.pow(dy, 2)
}

export function pointSegmentDist2(p: XY, a: XY, b: XY): number {
    const vx = b.x - a.x,
        vy = b.y - a.y
    const wx = p.x - a.x,
        wy = p.y - a.y
    const d2 = vx * vx + vy * vy
    const c = d2 === 0 ? 0 : (wx * vx + wy * vy) / d2
    if (c <= 0) return Math.pow(p.x - a.x, 2) + Math.pow(p.y - a.y, 2)
    if (c >= 1) return Math.pow(p.x - b.x, 2) + Math.pow(p.y - b.y, 2)
    const projX = a.x + c * vx
    const projY = a.y + c * vy
    return Math.pow(p.x - projX, 2) + Math.pow(p.y - projY, 2)
}

export function minDistToPolygonXY(bvh: BVHNode, p: XY): number {
    let best2 = Infinity
    const stack: BVHNode[] = [bvh]

    while (stack.length) {
        const node = stack.pop()!
        const d2Box = pointAABBDist2(p, node.bbox)
        if (d2Box >= best2) continue

        if (node.segments) {
            for (const s of node.segments) {
                const d2 = pointSegmentDist2(p, s.a, s.b)
                if (d2 < best2) best2 = d2
            }
        } else {
            const L = node.left,
                R = node.right
            const d2L = L ? pointAABBDist2(p, L.bbox) : Infinity
            const d2R = R ? pointAABBDist2(p, R.bbox) : Infinity

            if (d2L < d2R) {
                if (d2R < best2) stack.push(R!)
                if (d2L < best2) stack.push(L!)
            } else {
                if (d2L < best2) stack.push(L!)
                if (d2R < best2) stack.push(R!)
            }
        }
    }

    return Math.sqrt(best2)
}

export function minDistToPolygon(bvh: BVHNode, p: Coordinate): number {
    if (!bvh.center) throw new Error("BVH missing projection center, BVH must be a root node")
    const xy = project(p, bvh.center)
    return minDistToPolygonXY(bvh, xy)
}

const R = 6_371_000
const DEG = Math.PI / 180

export function centroid(poly: Coordinate[]): Coordinate {
    const sum = poly.reduce(
        (s, p) => ({ longitude: s.longitude + p.longitude, latitude: s.latitude + p.latitude }),
        { longitude: 0, latitude: 0 }
    )
    return { longitude: sum.longitude / poly.length, latitude: sum.latitude / poly.length }
}

export function project(p: Coordinate, center: Coordinate): XY {
    const φ1 = center.latitude * DEG
    const λ0 = center.longitude * DEG
    const φ = p.latitude * DEG
    const λ = p.longitude * DEG
    const Δλ = λ - λ0

    const cosc = Math.sin(φ1) * Math.sin(φ) + Math.cos(φ1) * Math.cos(φ) * Math.cos(Δλ)
    const c = Math.acos(Math.min(1, Math.max(-1, cosc)))

    const k = c === 0 ? 1 : c / Math.sin(c)

    return {
        x: R * k * Math.cos(φ) * Math.sin(Δλ),
        y: R * k * (Math.cos(φ1) * Math.sin(φ) - Math.sin(φ1) * Math.cos(φ) * Math.cos(Δλ)),
    }
}

export function buildBVH(polygon: Coordinate[], maxLeaf = 8): BVHNode {
    const center = centroid(polygon)

    const segments: Segment[] = polygon.map((p, i) => {
        const a = project(p, center)
        const next = polygon[(i + 1) % polygon.length]
        const b = project(next, center)
        return { a, b }
    })

    const root = buildBVHFromSegments(segments, maxLeaf)
    root.center = center
    return root
}

const cache = new WeakMap<Coordinate[], BVHNode>()

export const getBVH = (polygon: Coordinate[]) => {
    if (cache.get(polygon)) return cache.get(polygon)!
    const bvh = buildBVH(polygon)
    cache.set(polygon, bvh)
    return bvh
}
